import { supabase } from '../../utils/supabase'
import { TokenManager } from './tokenManager'
import { getAppBaseUrl } from '../../utils/appUrl'
import type { User } from '@supabase/supabase-js'

export interface AuthResult {
  user: User | null
  error: Error | null
}

export interface ResetPasswordResult {
  data: { message?: string } | null
  error: Error | null
}

export interface UpdatePasswordResult {
  success: boolean
  error: Error | null
}

export class AuthService {
  static async signIn(email: string, password: string): Promise<AuthResult> {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    // Clear any cached token to force fresh fetch
    TokenManager.clearToken()

    return { user: data.user, error }
  }

  static async signUp(
    email: string,
    password: string,
    fullName?: string,
    termsAccepted?: boolean,
    newsletterOptIn?: boolean,
    surveyData?: {
      surveyUserRole?: string
      surveyUserRoleOther?: string
      surveyUseCases?: string[]
      surveyIdeationFrequency?: string
      surveyCurrentTools?: string[]
      surveyCurrentToolsOther?: string
    }
  ): Promise<AuthResult> {
    const redirectTo = `${getAppBaseUrl()}/login`
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          full_name: fullName ?? '',
          terms_accepted: termsAccepted ?? false,
          terms_accepted_at: termsAccepted ? new Date().toISOString() : null,
          newsletter_opt_in: newsletterOptIn ?? false,
          // Only include survey fields that have actual values — sending null for
          // JSONB array fields causes "cannot extract elements from a scalar" in
          // the handle_new_user trigger because JSON null !== SQL NULL.
          ...(surveyData?.surveyUserRole != null && {
            survey_user_role: surveyData.surveyUserRole,
          }),
          ...(surveyData?.surveyUserRoleOther != null && {
            survey_user_role_other: surveyData.surveyUserRoleOther,
          }),
          ...(surveyData?.surveyUseCases != null && {
            survey_use_cases: surveyData.surveyUseCases,
          }),
          ...(surveyData?.surveyIdeationFrequency != null && {
            survey_ideation_frequency: surveyData.surveyIdeationFrequency,
          }),
          ...(surveyData?.surveyCurrentTools != null && {
            survey_current_tools: surveyData.surveyCurrentTools,
          }),
          ...(surveyData?.surveyCurrentToolsOther != null && {
            survey_current_tools_other: surveyData.surveyCurrentToolsOther,
          }),
        },
      },
    })
    return { user: data.user, error }
  }

  static async signUpAsPartner(
    email: string,
    password: string,
    fullName?: string,
    partnerTermsAccepted?: boolean,
    newsletterOptIn?: boolean,
    surveyData?: {
      surveyPromotionChannels?: string[]
      surveyPromotionChannelsOther?: string
      surveyAudienceSize?: string
      surveyNiche?: string
      surveyNicheOther?: string
      surveyExperience?: string
    }
  ): Promise<AuthResult> {
    const redirectTo = `${getAppBaseUrl()}/login`
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          full_name: fullName ?? '',
          // Partner-specific fields
          is_partner_signup: true,
          partner_terms_accepted: partnerTermsAccepted ?? false,
          partner_terms_accepted_at: partnerTermsAccepted ? new Date().toISOString() : null,
          newsletter_opt_in: newsletterOptIn ?? false,
          // Only include survey fields that have actual values (JSON null breaks JSONB array ops in trigger)
          ...(surveyData?.surveyPromotionChannels != null && {
            survey_promotion_channels: surveyData.surveyPromotionChannels,
          }),
          ...(surveyData?.surveyPromotionChannelsOther != null && {
            survey_promotion_channels_other: surveyData.surveyPromotionChannelsOther,
          }),
          ...(surveyData?.surveyAudienceSize != null && {
            survey_audience_size: surveyData.surveyAudienceSize,
          }),
          ...(surveyData?.surveyNiche != null && { survey_niche: surveyData.surveyNiche }),
          ...(surveyData?.surveyNicheOther != null && {
            survey_niche_other: surveyData.surveyNicheOther,
          }),
          ...(surveyData?.surveyExperience != null && {
            survey_experience: surveyData.surveyExperience,
          }),
        },
      },
    })
    return { user: data.user, error }
  }

  static async signInWithGoogle(): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getAppBaseUrl() },
    })
    return { error }
  }

  static async signInWithLinkedIn(): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'linkedin_oidc',
      options: { redirectTo: getAppBaseUrl() },
    })
    return { error }
  }

  static async signOut(): Promise<{ error: Error | null }> {
    const { error } = await supabase.auth.signOut()

    // Clear cached token on logout
    TokenManager.clearToken()

    return { error }
  }

  static async resetPassword(email: string): Promise<ResetPasswordResult> {
    const redirectTo = `${getAppBaseUrl()}/login`
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    })
    return { data, error }
  }

  static async updatePassword(newPassword: string): Promise<UpdatePasswordResult> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    })

    if (!error) {
      // Sign out after password update so user must log in with new password
      await supabase.auth.signOut()
      TokenManager.clearToken()
    }

    return { success: !error, error }
  }

  static async getCurrentUser(): Promise<User | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser()
    return user
  }

  static async refreshSession(): Promise<AuthResult> {
    const { data, error } = await supabase.auth.refreshSession()

    // Clear cached token to force fresh fetch
    TokenManager.clearToken()

    return { user: data.user, error }
  }
}
